import { format } from "date-fns"
import { useTranslation } from "react-i18next"
import { useEffect, useState } from "react"
import { GridColDef } from "@mui/x-data-grid"
import { useQueryClient } from "@tanstack/react-query"

import BasicButton from "@/components/button/BasicButton"
import Pagination from "@/components/table/Pagination"
import Table from "@/components/table/Table"
import {
  useCreateResendInvitationMutation,
  useDeleteInvitationMutation,
  useTeamInvitationsQuery,
} from "@/hooks/api/useInvitationQuery"
import Search from "@/components/form/search/Search"
import { PAGE_SIZE } from "@/constants/pagination"
import { createGlobalStore } from "@/stores/global"
import { FULL_DATE_FORMAT } from "@/constants/regex"
import { InvitationProps } from "@/app/settings/club/components/invitation/type"
import InvitationDialog from "@/app/settings/club/components/invitation/invitationDialog/InvitationDialog"
import InvitationResultDialog from "@/app/settings/club/components/invitation/invitationResultDialog/InvitationResultDialog"
import InvitationActionDialog from "@/app/settings/club/components/invitation/invitationActionDialog/InvitationActionDialog"

const EMAIL_MAX_WIDTH = 750
const DATE_MIN_WIDTH = 200
const ACTION_MAX_WIDTH = 254

export default function InvitationTab({ type }: InvitationProps) {
  const { t, i18n } = useTranslation("")

  const { data: invitations } = useTeamInvitationsQuery(type)

  const { mutateAsync: resendInvitation } = useCreateResendInvitationMutation()
  const { mutateAsync: removeInvitation } = useDeleteInvitationMutation()

  const { setSnackbar } = createGlobalStore((state) => ({
    setSnackbar: state.setSnackBar,
  }))

  const queryClient = useQueryClient()

  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false)
  const [isResultDialogOpen, setIsResultDialogOpen] = useState(false)
  const [keyword, setKeyword] = useState("")

  const [filteredInvitations, setFilteredInvitations] = useState(invitations)
  const [isActionDialogOpen, setIsActionDialogOpen] = useState(false)

  const [actionDialogType, setActionDialogType] = useState<"resend" | "remove">("resend")
  const [idfInvitation, setIdfInvitation] = useState<number>()
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
  })

  const columns: GridColDef[] = [
    {
      field: "email",
      renderHeader: () => t("email"),
      flex: 1,
      maxWidth: EMAIL_MAX_WIDTH,
    },
    {
      field: "dateCreated",
      renderHeader: () => t("date"),
      flex: 0,
      minWidth: DATE_MIN_WIDTH,
      renderCell: (params) => format(new Date(params.row.dateCreated), FULL_DATE_FORMAT),
    },
    {
      field: "action",
      headerName: "",
      disableColumnMenu: true,
      disableReorder: true,
      sortable: false,
      hideSortIcons: true,
      display: "flex",
      flex: 1,
      maxWidth: ACTION_MAX_WIDTH,
      align: "center",
      renderCell: ({ row }) => {
        return (
          <div className="InvitationTableButtonCellCont">
            <BasicButton
              type="default-outlined"
              size="sm"
              onClick={() => {
                setIdfInvitation(row.idfInvitation)
                setIsActionDialogOpen(true)
                setActionDialogType("resend")
              }}>
              {t("button.resend")}
            </BasicButton>
            <BasicButton
              type="danger-outlined"
              size="sm"
              onClick={() => {
                setIdfInvitation(row.idfInvitation)
                setIsActionDialogOpen(true)
                setActionDialogType("remove")
              }}>
              {t("button.remove")}
            </BasicButton>
          </div>
        )
      },
    },
  ]

  const afterEmailSent = () => {
    setIsResultDialogOpen(true)
    setIsInviteDialogOpen(false)
  }

  const confirmResultDialog = () => {
    setIsResultDialogOpen(false)
    queryClient.invalidateQueries({ queryKey: ["readTeamInvitations", type] })
  }

  useEffect(() => {
    if (invitations) {
      setFilteredInvitations(
        invitations.filter(
          (invitation) =>
            invitation.email.includes(keyword) ||
            format(new Date(invitation.dateCreated), FULL_DATE_FORMAT).includes(keyword),
        ),
      )
    }
  }, [keyword, invitations])

  const resendEmail = () => {
    if (idfInvitation !== undefined) {
      resendInvitation({
        idfInvitation,
        language: i18n.language,
        env: import.meta.env.VITE_MODE as string,
      })
        .then(() => {
          setSnackbar({ message: t("successSentEmail"), severity: "success", open: true })
          setIsActionDialogOpen(false)
        })
        .catch(() => {
          setIsActionDialogOpen(false)
        })
    }
  }

  const removeInvitationHandler = () => {
    if (idfInvitation !== undefined) {
      removeInvitation(idfInvitation)
        .then(() => {
          setSnackbar({ message: t("messages.successDelete"), severity: "success", open: true })
          setIsActionDialogOpen(false)
          queryClient.invalidateQueries({ queryKey: ["readTeamInvitations", type] })
        })
        .catch(() => {
          setIsActionDialogOpen(false)
        })
    }
  }

  const resendInvitationFooter = () => (
    <div className="invitation-action-footer">
      <BasicButton type="default-outlined" size="md" onClick={() => setIsActionDialogOpen(false)}>
        {t("button.cancel")}
      </BasicButton>
      <BasicButton type="primary-contained" size="md" onClick={resendEmail}>
        {t("button.send")}
      </BasicButton>
    </div>
  )

  const removeInvitationFooter = () => (
    <div className="invitation-action-footer">
      <BasicButton type="default-outlined" size="md" onClick={() => setIsActionDialogOpen(false)}>
        {t("button.cancel")}
      </BasicButton>
      <BasicButton type="danger-outlined" size="md" onClick={removeInvitationHandler}>
        {t("button.remove")}
      </BasicButton>
    </div>
  )

  return (
    <div className="InvitationTabCont">
      <div className="search-and-button">
        <Search onChange={setKeyword} />
        <BasicButton type="default-outlined" size="md" onClick={() => setIsInviteDialogOpen(true)}>
          {t("button.invite")}
        </BasicButton>
      </div>
      <Table
        columns={columns}
        rows={filteredInvitations || []}
        getRowId={(item) => item.email}
        disableColumnResize
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
          sorting: {
            sortModel: [{ field: "dateCreated", sort: "desc" }],
          },
        }}
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        slots={{ pagination: filteredInvitations?.length ? Pagination : null }}
      />
      <InvitationDialog
        open={isInviteDialogOpen}
        onClose={() => setIsInviteDialogOpen(false)}
        onConfirm={afterEmailSent}
        type={type}
      />
      <InvitationResultDialog
        open={isResultDialogOpen}
        onClose={confirmResultDialog}
        type={type}
        onConfirm={() => undefined}
      />
      <InvitationActionDialog
        open={isActionDialogOpen}
        onClose={() => setIsActionDialogOpen(false)}
        footer={(actionDialogType === "resend"
          ? resendInvitationFooter
          : removeInvitationFooter)()}>
        {t(
          actionDialogType === "resend" ? "messages.confirmResendEmail" : "messages.confirmDelete",
        )}
      </InvitationActionDialog>
    </div>
  )
}
